import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from 'rxjs/operators';
import { IsKakaoPhoneAgreedResponse, KakaoShareData, KakaoSyncTerms, KakaoSyncTermsResponse } from "src/app/interfaces/kakao";
import { RouterService } from 'src/app/services/router/router.service';
import { UserService } from 'src/app/services/user/user.service';
import { WebviewService } from 'src/app/services/webview/webview.service';
import { environment } from "src/environments/environment";
import { isCabinetLink } from "../cabinet/cabinet.service";
import { CustomHttpClient } from "../http/http.service";
import { SnackbarService } from "../snackbar/snackbar.service";

declare const Kakao: any;

@Injectable({
  providedIn: "root"
})
export class KakaoService {
  constructor(
    private snackbarService: SnackbarService,
    private http: CustomHttpClient,
    private userService: UserService,
    private routerService: RouterService,
    private webviewService: WebviewService
  ) {
    this.initializeKakao();
  }

  receivedKakaoAuthCodeList: string[] = [];
  phoneNumberAgreeChanged = false;

  get isKakaoInitialized(): boolean {
    return Kakao.isInitialized();
  }

  /**
   * initialize kakao
   */
  initializeKakao(): void {
    if (Kakao === undefined || this.isKakaoInitialized) {
      return;
    }
    Kakao.init(environment.kakaoWebKey);
  }

  /**
   * kakao user model은 아래 링크에서 확인
   * https://developers.kakao.com/sdk/reference/android-rx/release/kakao-android-sdk-rx/com.kakao.sdk.user.model/-user/index.html
   */
  async login(url?: string, notAllowedServiceTerms: string[] = []): Promise<boolean> {
    if (!this.isKakaoInitialized) {
      this.initializeKakao();
    }
    const authParam: { redirectUri: string; scope?: string; state?: string; serviceTerms?: string } = {
      redirectUri: `${isCabinetLink ? environment.cabinetWebUrl : environment.webUrl}/oauth/kakao/login`
    };
    const previousUrl: string | undefined = this.routerService.getPreviousUrl();
    if (url) {
      authParam.state = encodeURIComponent(url);
    } else if (previousUrl) {
      // 로그인 전에 있었던 주소(위치)
      authParam.state = previousUrl;
    }
    if (notAllowedServiceTerms.length > 0) {
      authParam.serviceTerms = notAllowedServiceTerms.join(',');
    }
    Kakao.Auth.authorize(authParam);
    return true;
  }

  agreeKakaoFriends(url: string): void {
    if (!this.isKakaoInitialized) {
      this.initializeKakao();
    }
    const authParam: { redirectUri: string; scope?: string; state?: string; } = {
      redirectUri: `${environment.webUrl}/oauth/kakao/friend`,
      scope: 'friends',
      state: url
    };
    Kakao.Auth.authorize(authParam);
  }

  agreePhoneNumber(url: string): void {
    if (!this.isKakaoInitialized) {
      this.initializeKakao();
    }
    const authParam: { redirectUri: string; scope?: string; state?: string; } = {
      redirectUri: `${environment.webUrl}/oauth/kakao/phone`,
      scope: 'phone_number',
      state: url
    };
    Kakao.Auth.authorize(authParam);
  }
  
  getKakaoProfile(): Observable<{ data: { profileImageUrl: string }}> {
    return this.http.get<{ data: { profileImageUrl: string }}>(`${environment.apiUrl}/v1/profile/kakao`);
  }

  /**
   * 현재 유저가 동의한 권한 목록
   * KakaoScope[]
   */
  getIsKakaoPhoneAgreed(): Observable<IsKakaoPhoneAgreedResponse> {
    return this.http.get<IsKakaoPhoneAgreedResponse>(`${environment.apiUrl}/v1/oauth/kakao/scopes/phone-number`);
  }

  /** 카카오 전화번호로 유저 전화번호 업데이트 */
  updatePhoneFromKakao(): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/v1/user/phone-number/kakao`, {});
  }

  getKakaoUserServiceTerms(): Observable<KakaoSyncTerms> {
    return this.http.get<KakaoSyncTermsResponse>(`${environment.apiUrl}/v1/oauth/kakao/terms`)
    .pipe(map((res) => (res.data)));
  }

  onKakaoTokenExpired(currentUrl?: string): void {
    this.snackbarService.openSnackBar('로그인 세션이 만료되었습니다. 다시 로그인해 주세요.');
    const loginRetryTimeout = setTimeout(() => {
      clearTimeout(loginRetryTimeout);
    }, 500);
  }

  /**
   * success Callback이 없기 때문에, 3초 뒤에도 fail callback이 없을 경우 true로 리턴
   * 공유창 뜨고 공유하기까지 빨라야 3초일 것으로 예상. 그때까진 로딩 계속 보여줘도 상관없을듯.
   */
  async shareQuizUsingKakao(data: KakaoShareData): Promise<boolean> {
    if (!this.isKakaoInitialized) {
      this.initializeKakao();
    }
    // 웹뷰일 경우
    if (this.webviewService.isWebView) {
      this.webviewService.postKakaoShareMessage(data);
      return true;
    }
    let succeed = true;
    return new Promise((resolve, reject) => {
      const { title, description, imageUrl, imageLinkUrl, imageWidth, imageHeight, buttonTitle } = data;
      Kakao.Link.sendDefault({
        objectType: 'feed',
        content: {
          title,
          description,
          imageUrl,
          imageWidth,
          imageHeight,
          link: {
            mobileWebUrl: imageLinkUrl,
            webUrl: imageLinkUrl
          },
        },
        buttons: [
          {
            title: buttonTitle,
            link: {
              mobileWebUrl: imageLinkUrl,
              webUrl: imageLinkUrl
            },
          }
        ],
        fail: (error: any) => {
          // if (error?.code === -10) {
          //   this.snackbarService.openSnackBar('일일 한도에 도달해 현재 카톡 공유를 이용할 수 없습니다. 링크 복사를 이용해 주세요.');
          // }
          succeed = false;
          reject(false);
        }
      });
      const failTimeout = setTimeout(() => {
        succeed ? resolve(true) : reject(false);
        clearTimeout(failTimeout);
      }, 3000);
    });
  }

  updateFriendAgreement(code: string): Observable<void> {
    return this.http.post(`${environment.apiUrl}/v1/oauth/kakao/friend`, { code }, { loggingText: 'kakao friend agreement and save kakao friends(only for web)'});
  }

  updatePhoneAgreement(code: string): Observable<void> {
    return this.http.post(`${environment.apiUrl}/v1/oauth/kakao/phone`, { code });
  }

  revokeTerms(terms: string[]): Observable<void> {
    return this.http.post(`${environment.apiUrl}/v1/oauth/kakao/revoke`, {
      scopes: terms
    });
  }

  async syncPhoneFromKakao(): Promise<'succeed' | 'disagreed' | 'not_registered'> {
    const isAgreedResult = await this.getIsKakaoPhoneAgreed().toPromise()
    if (isAgreedResult.data.isAgreed) {
      await this.updatePhoneFromKakao().toPromise();
      await this.userService.getProfile().toPromise();
    } else {
      return 'disagreed';
    }
    if (this.userService.user.phoneNumber) {
      return 'succeed';
    } else {
      return 'not_registered';
    }
  }

  async navigateAfterSyncPhone(result: 'succeed' | 'disagreed' | 'not_registered'): Promise<void> {
    switch (result) {
      case 'succeed':
        this.routerService.navigate('signal/main');
        return;
      case 'disagreed':
        this.routerService.navigate('signal/auth/agree');
        return;
      case 'not_registered':
        this.routerService.navigate('signal/auth/register');
        return;
    }
  }
}
